import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePermissions } from '../../../contexts/PermissionContext';
import { Eventbus } from '../../../utils';
import { events } from '../../../utils/constants';
import logger from '../../../utils/logger';
import {
	DASHBOARD_SOURCE,
	DASHBOARD_SOURCES,
	DASHBOARD_SOURCE_KEYS,
	fetchDashboardSource,
	isMessageInScope,
	mergeCentreStatusMessage,
} from '../dashboard.service';

const REFETCH_DEBOUNCE_MS = 1500;
// Time-driven changes (hold expiry, day/week rollover, tours becoming overdue) raise no SignalR
// message, so refresh periodically while the tab is visible.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

const INITIAL_SOURCE_STATE = { data: null, loading: true, refreshing: false, error: null };

const initialState = () =>
	DASHBOARD_SOURCE_KEYS.reduce((acc, key) => ({ ...acc, [key]: INITIAL_SOURCE_STATE }), {});

// Loads every dashboard endpoint independently (one slow or failed endpoint never blocks the
// others) and keeps each one live: SignalR messages refetch only the endpoint they affect.
export default function useDashboardData() {
	const { hasAllCentreAccess, isCentreAuthorised } = usePermissions();
	const [sources, setSources] = useState(initialState);

	const mountedRef = useRef(true);
	const requestSeqRef = useRef({});
	const timersRef = useRef({});
	const scopeRef = useRef({ hasAllCentreAccess, isCentreAuthorised });
	useEffect(() => { scopeRef.current = { hasAllCentreAccess, isCentreAuthorised }; }, [hasAllCentreAccess, isCentreAuthorised]);

	const patchSource = useCallback((key, patch) => {
		if (!mountedRef.current) return;
		setSources((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
	}, []);

	const load = useCallback(async (key) => {
		const seq = (requestSeqRef.current[key] ?? 0) + 1;
		requestSeqRef.current[key] = seq;
		const isLatest = () => mountedRef.current && requestSeqRef.current[key] === seq;

		setSources((prev) => ({
			...prev,
			[key]: { ...prev[key], loading: prev[key].data == null, refreshing: prev[key].data != null, error: null },
		}));

		try {
			const data = await fetchDashboardSource(key);
			if (!isLatest()) return;
			patchSource(key, data === undefined
				? { loading: false, refreshing: false }
				: { data, loading: false, refreshing: false, error: null });
		} catch (error) {
			if (!isLatest()) return;
			logger.error(`Dashboard: failed to load ${key}`, error);
			patchSource(key, { loading: false, refreshing: false, error });
		}
	}, [patchSource]);

	const loadAll = useCallback(() => DASHBOARD_SOURCE_KEYS.forEach((key) => load(key)), [load]);

	const scheduleRefetch = useCallback((key) => {
		clearTimeout(timersRef.current[key]);
		timersRef.current[key] = setTimeout(() => load(key), REFETCH_DEBOUNCE_MS);
	}, [load]);

	// Initial load: all endpoints in parallel, each settling on its own.
	useEffect(() => {
		mountedRef.current = true;
		loadAll();
		return () => {
			mountedRef.current = false;
			Object.values(timersRef.current).forEach(clearTimeout);
			timersRef.current = {};
		};
	}, [loadAll]);

	// SignalR: one handler per endpoint/event pair, subscribed on mount and removed on unmount.
	useEffect(() => {
		const handlers = DASHBOARD_SOURCE_KEYS.flatMap((key) => {
			const { events: sourceEvents, eventFilters = {} } = DASHBOARD_SOURCES[key];
			return sourceEvents.map((eventName) => {
				const accepts = eventFilters[eventName];
				const handler = (message) => {
					if (accepts && !accepts(message)) return;
					if (isMessageInScope(message, scopeRef.current)) scheduleRefetch(key);
				};
				Eventbus.on(eventName, handler);
				return handler;
			});
		});

		const handleCentreStatus = (message) => {
			setSources((prev) => {
				const current = prev[DASHBOARD_SOURCE.CENTRE_STATUS];
				const merged = mergeCentreStatusMessage(current.data, message);
				return merged === current.data ? prev : { ...prev, [DASHBOARD_SOURCE.CENTRE_STATUS]: { ...current, data: merged } };
			});
		};
		Eventbus.on(events.SIGNALR.CENTRE_UPDATED, handleCentreStatus);

		// After a reconnect, messages may have been missed: refresh everything once.
		let disconnected = false;
		const handleDisconnected = () => { disconnected = true; };
		const handleReconnected = () => {
			if (!disconnected) return;
			disconnected = false;
			DASHBOARD_SOURCE_KEYS.forEach(scheduleRefetch);
		};
		Eventbus.on(events.SIGNALR.DISCONNECTED, handleDisconnected);
		Eventbus.on(events.SIGNALR.CONNECTED, handleReconnected);

		return () => {
			handlers.forEach((handler) => Eventbus.off(handler));
			Eventbus.off(handleCentreStatus);
			Eventbus.off(handleDisconnected);
			Eventbus.off(handleReconnected);
		};
	}, [scheduleRefetch]);

	// Light poll while visible, plus a refresh when the tab becomes visible again.
	useEffect(() => {
		if (typeof document === 'undefined') return undefined;
		const isVisible = () => document.visibilityState === 'visible';

		const interval = setInterval(() => { if (isVisible()) loadAll(); }, POLL_INTERVAL_MS);
		const handleVisibility = () => { if (isVisible()) DASHBOARD_SOURCE_KEYS.forEach(scheduleRefetch); };
		document.addEventListener('visibilitychange', handleVisibility);

		return () => {
			clearInterval(interval);
			document.removeEventListener('visibilitychange', handleVisibility);
		};
	}, [loadAll, scheduleRefetch]);

	return useMemo(() => ({ sources, reload: load }), [sources, load]);
}
